/**
 * TOD International Technical Cup — Pool Standings Calculator
 * 
 * Section 4.2 ranking criteria (evaluated sequentially):
 * 1. P/I (Punteggio Incontri / Bout Score): Win = 3pt, Tie = 1pt, Defeat = 0pt
 * 2. P/R (Punteggio Riprese / Round Score): Sum of all judge scores across all rounds
 * 3. Tiebreaker bout (flagged, not auto-resolved)
 * 
 * Pool normalization: when pools have different sizes, results are proportioned
 * to the average pool size.
 */

/**
 * Calculate standings for a single pool.
 * @param {string[]} fighters - list of fighter names in the pool
 * @param {object[]} bouts - confirmed bouts in this pool (must have confirmedResult)
 * @returns {object[]} sorted standings array
 */
export function calculatePoolStandings(fighters, bouts) {
  // Initialize stats per fighter
  const stats = {};
  fighters.forEach(name => {
    stats[name] = {
      name,
      bouts: 0,
      wins: 0,
      ties: 0,
      losses: 0,
      PI: 0,        // Bout Score (P/I): win=3, tie=1, loss=0
      PR: 0,        // Round Score (P/R): sum of all judge scores received
      PRagainst: 0,  // Round Score against (for reference)
    };
  });

  // Process each confirmed bout
  const confirmedBouts = bouts.filter(b => b.status === "confirmed" && b.confirmedResult);

  confirmedBouts.forEach(bout => {
    const result = bout.confirmedResult;
    const blue = bout.fighterBlue;
    const red = bout.fighterRed;
    const scoreBlue = result.scoreB ?? 0;  // scoreB = blue's score
    const scoreRed = result.scoreA ?? 0;   // scoreA = red's score

    // Make sure both fighters are in our stats (safety check)
    if (!stats[blue] || !stats[red]) return;

    stats[blue].bouts++;
    stats[red].bouts++;
    stats[blue].PR += scoreBlue;
    stats[red].PR += scoreRed;
    stats[blue].PRagainst += scoreRed;
    stats[red].PRagainst += scoreBlue;

    if (scoreBlue > scoreRed) {
      // Blue wins
      stats[blue].wins++;
      stats[blue].PI += 3;
      stats[red].losses++;
      // Red gets 0
    } else if (scoreRed > scoreBlue) {
      // Red wins
      stats[red].wins++;
      stats[red].PI += 3;
      stats[blue].losses++;
      // Blue gets 0
    } else {
      // Tie
      stats[blue].ties++;
      stats[blue].PI += 1;
      stats[red].ties++;
      stats[red].PI += 1;
    }
  });

  // Sort by P/I desc, then P/R desc
  const standings = Object.values(stats).sort((a, b) => {
    // 1. P/I (Bout Score) — higher is better
    if (b.PI !== a.PI) return b.PI - a.PI;
    // 2. P/R (Round Score) — higher is better
    if (b.PR !== a.PR) return b.PR - a.PR;
    // 3. Still tied — flag for tiebreaker bout
    return 0;
  });

  // Add rank and tiebreaker flag
  standings.forEach((s, i) => {
    s.rank = i + 1;
    // Check if tied with adjacent entries on both P/I and P/R
    s.tiedWithPrev = i > 0 && standings[i - 1].PI === s.PI && standings[i - 1].PR === s.PR;
    s.tiedWithNext = i < standings.length - 1 && standings[i + 1].PI === s.PI && standings[i + 1].PR === s.PR;
    s.needsTiebreaker = s.tiedWithPrev || s.tiedWithNext;
  });

  // Assign same rank to tied fighters
  for (let i = 1; i < standings.length; i++) {
    if (standings[i].tiedWithPrev) {
      standings[i].rank = standings[i - 1].rank;
    }
  }

  return standings;
}

/**
 * Calculate normalized standings across pools of different sizes.
 * Per Section 4.2: results are proportioned to the average pool size.
 * 
 * Normalization formula:
 *   normalized_value = (raw_value / actual_pool_bouts) * average_pool_bouts
 * 
 * @param {object[]} allPools - [{ name, fighters, bouts }]
 * @returns {object[]} combined, normalized, sorted standings
 */
export function calculateNormalizedStandings(allPools) {
  if (allPools.length <= 1 && allPools[0]) {
    return calculatePoolStandings(allPools[0].fighters, allPools[0].bouts);
  }

  // Derive bouts-per-fighter for each pool from the actual confirmed bout
  // data rather than assuming N-1 (a single round-robin) — this makes
  // normalization work unchanged whether a pool ran once, twice (double
  // round-robin), or is missing a bout or two.
  const poolResults = allPools.map(pool => {
    const poolStandings = calculatePoolStandings(pool.fighters, pool.bouts);
    const totalBouts = poolStandings.reduce((sum, s) => sum + s.bouts, 0);
    const boutsPerFighter = poolStandings.length ? totalBouts / poolStandings.length : 0;
    return { pool, poolStandings, boutsPerFighter };
  });

  const avgBoutsPerFighter =
    poolResults.reduce((sum, p) => sum + p.boutsPerFighter, 0) / poolResults.length;

  const allStandings = [];

  poolResults.forEach(({ pool, poolStandings, boutsPerFighter }) => {
    const normFactor = boutsPerFighter > 0 ? avgBoutsPerFighter / boutsPerFighter : 1;

    poolStandings.forEach(s => {
      allStandings.push({
        ...s,
        poolName: pool.name,
        rawPI: s.PI,
        rawPR: s.PR,
        // Normalize P/I and P/R
        PI: Math.round(s.PI * normFactor * 100) / 100,
        PR: Math.round(s.PR * normFactor * 100) / 100,
        normalized: normFactor !== 1,
      });
    });
  });

  // Sort all fighters across pools
  allStandings.sort((a, b) => {
    if (b.PI !== a.PI) return b.PI - a.PI;
    if (b.PR !== a.PR) return b.PR - a.PR;
    return 0;
  });

  // Assign overall rank
  allStandings.forEach((s, i) => {
    s.rank = i + 1;
    s.tiedWithPrev = i > 0 && allStandings[i - 1].PI === s.PI && allStandings[i - 1].PR === s.PR;
    s.tiedWithNext = i < allStandings.length - 1 && allStandings[i + 1].PI === s.PI && allStandings[i + 1].PR === s.PR;
    s.needsTiebreaker = s.tiedWithPrev || s.tiedWithNext;
  });

  for (let i = 1; i < allStandings.length; i++) {
    if (allStandings[i].tiedWithPrev) {
      allStandings[i].rank = allStandings[i - 1].rank;
    }
  }

  return allStandings;
}

/**
 * Combine standings across one or two pool rounds. Each pool object may
 * carry a `round` (1 or 2, default 1). Pools are grouped by round, normalized within
 * their own round via calculateNormalizedStandings, and — when a second
 * round exists — summed per fighter (by name) into a single combined
 * ranking. With only round 1 present, this returns exactly what
 * calculateNormalizedStandings/calculatePoolStandings already returned, so
 * existing single-pool-round disciplines are unaffected.
 *
 * @param {object[]} pools - discipline.pools, each { name, fighters, round? }
 * @param {object[]} bouts - all bouts for the discipline (any phase/status; filtered here)
 * @returns {object[]} combined, ranked standings
 */
export function calculateCombinedStandings(pools, bouts) {
  const confirmedBouts = (bouts || []).filter(b => b.status === "confirmed" && b.confirmedResult);

  const byRound = {};
  (pools || []).forEach(p => {
    const r = p.round || 1;
    (byRound[r] = byRound[r] || []).push(p);
  });
  const roundKeys = Object.keys(byRound).map(Number).sort((a, b) => a - b);
  if (roundKeys.length === 0) return [];

  const perRound = roundKeys.map(r => {
    const poolData = byRound[r].map(pool => ({
      name: pool.name,
      fighters: pool.fighters,
      bouts: confirmedBouts.filter(b => b.poolName === pool.name),
    }));
    return poolData.length === 1
      ? calculatePoolStandings(poolData[0].fighters, poolData[0].bouts)
      : calculateNormalizedStandings(poolData);
  });

  if (roundKeys.length === 1) return perRound[0];

  // Sum each fighter's normalized PI/PR across rounds
  const merged = {};
  perRound.forEach((standings, idx) => {
    standings.forEach(s => {
      const m = merged[s.name] || {
        name: s.name, bouts: 0, wins: 0, ties: 0, losses: 0,
        PI: 0, PR: 0, PRagainst: 0, poolNames: [],
      };
      m.bouts += s.bouts;
      m.wins += s.wins;
      m.ties += s.ties;
      m.losses += s.losses;
      m.PI += s.PI;
      m.PR += s.PR;
      m.PRagainst += s.PRagainst;
      m.poolNames.push(s.poolName || `Round ${roundKeys[idx]}`);
      merged[s.name] = m;
    });
  });

  const allStandings = Object.values(merged).map(m => ({
    ...m,
    PI: Math.round(m.PI * 100) / 100,
    PR: Math.round(m.PR * 100) / 100,
    poolName: m.poolNames.join(" / "),
    normalized: true,
  }));

  allStandings.sort((a, b) => {
    if (b.PI !== a.PI) return b.PI - a.PI;
    if (b.PR !== a.PR) return b.PR - a.PR;
    return 0;
  });

  allStandings.forEach((s, i) => {
    s.rank = i + 1;
    s.tiedWithPrev = i > 0 && allStandings[i - 1].PI === s.PI && allStandings[i - 1].PR === s.PR;
    s.tiedWithNext = i < allStandings.length - 1 && allStandings[i + 1].PI === s.PI && allStandings[i + 1].PR === s.PR;
    s.needsTiebreaker = s.tiedWithPrev || s.tiedWithNext;
  });
  for (let i = 1; i < allStandings.length; i++) {
    if (allStandings[i].tiedWithPrev) allStandings[i].rank = allStandings[i - 1].rank;
  }

  return allStandings;
}

/**
 * Snake-seed a ranked list of fighters into N pools (1st -> pool 0, 2nd ->
 * pool 1, ..., Nth -> pool N-1, then reverse direction for the next row).
 * Used to re-draw pools for round 2 from round 1 standings so strength is
 * spread evenly across the new groupings.
 *
 * @param {string[]} rankedNames - fighter names in rank order (best first)
 * @param {number} poolCount
 * @returns {string[][]} one array of fighter names per pool
 */
export function snakeSeedIntoPools(rankedNames, poolCount) {
  const groups = Array.from({ length: poolCount }, () => []);
  let i = 0;
  let forward = true;
  while (i < rankedNames.length) {
    const order = forward ? [...Array(poolCount).keys()] : [...Array(poolCount).keys()].reverse();
    for (const g of order) {
      if (i >= rankedNames.length) break;
      groups[g].push(rankedNames[i]);
      i++;
    }
    forward = !forward;
  }
  return groups;
}

/**
 * Determine the elimination bracket size (power of 2) and cutoff.
 * Per Section 4.2: 35-60% of total athletes advance to a perfect bracket.
 * 
 * @param {number} totalFighters - total number of fighters across all pools
 * @returns {{ bracketSize: number, advancing: number, percentage: number }}
 */
export function calculateBracketSize(totalFighters) {
  const powers = [4, 8, 16, 32, 64, 128];
  
  for (const size of powers) {
    const pct = (size / totalFighters) * 100;
    if (pct >= 35 && pct <= 60) {
      return { bracketSize: size, advancing: size, percentage: Math.round(pct) };
    }
  }

  // If no perfect fit, find the closest power of 2 within range
  let best = null;
  for (const size of powers) {
    const pct = (size / totalFighters) * 100;
    if (pct >= 25 && pct <= 75) {
      if (!best || Math.abs(pct - 50) < Math.abs(best.percentage - 50)) {
        best = { bracketSize: size, advancing: size, percentage: Math.round(pct) };
      }
    }
  }

  return best || { bracketSize: Math.min(8, totalFighters), advancing: Math.min(8, totalFighters), percentage: Math.round((Math.min(8, totalFighters) / totalFighters) * 100) };
}
